import { Button, DestructiveConfirmDialog, TimelineInteractiveCard, useUncontrolledState } from "@tellescope/ui";
import { TimelineCardBaseProps } from "../types";
import { Copy, Eye, MessageCircle, Pin, Send, Tag, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useAuth,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { useState } from "react";
import { ChatRoomContextCard } from "@tellescope/messaging";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AddTagContent } from "../AddTagContent";

export interface ChatRoomTimelineCardProps extends TimelineCardBaseProps<"chat_rooms"> {}

export const ChatRoomTimelineCard = ({ item: chatRoom, className, open, onOpenChange }: ChatRoomTimelineCardProps) => {
  const { t } = useTranslation("timeline");
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useUncontrolledState<boolean>({
    state: open ?? false,
    setState: onOpenChange && ((next) => onOpenChange(next instanceof Function ? next(open ?? false) : next)),
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    delete: { access: canDelete },
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "chat_rooms",
    actions: ["delete", "update"],
  });
  const {
    create: { access: canReply },
  } = usePermissionQuery({
    model: "chats",
    actions: ["create"],
  });

  const deleteChatRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "deleteOne",
  });

  const updateChatRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "updateOne",
  });

  const threadIdQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      mdbFilter: {
        type: "Chat",
        threadId: chatRoom.id,
      },
      limit: 1,
    },
    getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.flatMap(({ threads }) => threads).at(0)?.id,
  });

  const isUnread = currentUser
    ? !!chatRoom.infoForUser?.[currentUser.id]?.unreadCount || !!chatRoom.markedUnreadForAll
    : false;

  return (
    <>
      <TimelineInteractiveCard
        icon={MessageCircle}
        title={chatRoom.title || chatRoom.topic || t("Chat")}
        timestamp={chatRoom.recentMessageSentAt || chatRoom.createdAt}
        isPinned={!!chatRoom.pinnedAt}
        isUnread={isUnread}
        disablePreview
        onHeaderClick={isOpen ? undefined : () => setIsOpen(true)}
        preview={{
          content: chatRoom.recentMessage || chatRoom.description || "",
        }}
        tags={chatRoom.tags}
        primaryAction={
          isOpen
            ? [
                {
                  label: t("Reply"),
                  content:
                    threadIdQuery.data && canReply ? (
                      <Link to={`/messaging/inbox/chat/${threadIdQuery.data}`}>
                        <Button size="sm" className="max-h-[30px]">
                          <Send />
                          {t("Reply")}
                        </Button>
                      </Link>
                    ) : (
                      <div />
                    ),
                },
                {
                  label: t("Collapse"),
                  onClick: () => {
                    setIsOpen(false);
                  },
                },
              ]
            : undefined
        }
        actions={[
          [
            {
              icon: Eye,
              label: t("View Messages"),
              onClick: () => {
                setIsOpen(true);
              },
            },
            {
              icon: Trash2,
              label: t("Delete Chat"),
              onClick: () => {
                setIsDeleting(true);
              },
              hidden: !canDelete,
            },
          ],
          [
            {
              icon: Copy,
              label: t("Copy ID"),
              onClick: async () => {
                await navigator.clipboard.writeText(chatRoom.id);
                toast(t("Chat ID copied"), {
                  description: t("{{chatId}} has been copied to clipboard", { chatId: chatRoom.id }),
                });
              },
            },
          ],
          [
            {
              icon: Tag,
              label: t("Add Tag"),
              submenu: (
                <AddTagContent
                  currentTags={chatRoom.tags ?? []}
                  onTagClick={async (tag) => {
                    if (!chatRoom.tags?.includes(tag)) {
                      await updateChatRoomMutation.mutateAsync(
                        {
                          id: chatRoom.id,
                          tags: [...(chatRoom.tags ?? []), tag],
                        },
                        {
                          onError: () => {
                            toast.error(t("Error tagging. Please try again."));
                          },
                        }
                      );
                    } else {
                      await updateChatRoomMutation.mutateAsync(
                        {
                          id: chatRoom.id,
                          tags: chatRoom.tags?.filter((name) => name !== tag) ?? [],
                          _options: {
                            replaceObjectFields: true,
                          },
                        },
                        {
                          onError: () => {
                            toast.error(t("Error removing tag. Please try again."));
                          },
                        }
                      );
                    }
                  }}
                  model="chat_rooms"
                />
              ),
              hidden: !canUpdate,
            },
            {
              icon: Pin,
              fillIcon: !!chatRoom.pinnedAt,
              label: chatRoom.pinnedAt ? t("Remove Pin") : t("Pin"),
              onClick: () => {
                updateChatRoomMutation.mutateAsync({
                  id: chatRoom.id,
                  pinnedAt: chatRoom.pinnedAt ? "" : new Date(),
                });
              },
              hidden: !canUpdate,
            },
          ],
        ]}
        className={className}
      >
        {!!isOpen && <ChatRoomContextCard chatRoomId={chatRoom.id} />}
      </TimelineInteractiveCard>
      <DestructiveConfirmDialog
        onConfirm={() => {
          deleteChatRoomMutation.mutateAsync(chatRoom.id);
        }}
        onCancel={() => setIsDeleting(false)}
        onOpenChange={setIsDeleting}
        open={isDeleting}
        description={t("This will permanently delete the chat room. This action cannot be undone.")}
      />
    </>
  );
};
